import { useCallback, useMemo, useState } from 'react'


function useStep({ maxStep, minStep = 0, initialStep = 0 }) {
    const [currentStep, setCurrentStep] = useState(initialStep)

    const isFirstStep = currentStep === minStep;
    const isLastStep = currentStep === maxStep;

    const canGoToNextStep = useMemo(
        () => currentStep + 1 <= maxStep,
        [currentStep],
    )

    const canGoToPrevStep = useMemo(() => currentStep - 1 >= minStep, [currentStep])

    const setStep = useCallback(
        step => {
            // Allow value to be a function so we have the same API as useState
            const newStep = step instanceof Function ? step(currentStep) : step

            if (newStep >= 1 && newStep <= maxStep) {
                setCurrentStep(newStep)
                return
            }

            throw new Error('Step not valid')
        },
        [maxStep, currentStep],
    )

    const goToNextStep = useCallback(() => {
        if (canGoToNextStep) {
            setCurrentStep(step => step + 1)
        }
    }, [canGoToNextStep])

    const goToPrevStep = useCallback(() => {
        if (canGoToPrevStep) {
            setCurrentStep(step => step - 1)
        }
    }, [canGoToPrevStep])

    const reset = useCallback(() => {
        setCurrentStep(initialStep)
    }, [])

    return [
        currentStep,
        {
            goToNextStep,
            goToPrevStep,
            canGoToNextStep,
            canGoToPrevStep,
            isFirstStep,
            isLastStep,
            setStep,
            reset
        },
    ]
}

export default useStep
